import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withApollo } from 'react-apollo';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import Modal from '../Global/Modals/Modal';
import OutlinedSelectInput from '../Global/Inputs/OutlinedSelect';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdfOutlined';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibraryOutlined';
import ProgressButton from '../Global/Buttons/Progress';
import Button from '@material-ui/core/Button';
import { Formik, Field } from 'formik';
import Grid from '@material-ui/core/Grid';
import LoadingIndicator from '../LoadingIndicator';
import Autocomplete from '../Global/Inputs/Autocomplete';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import ButtonStyles from '../../styles/button';
import SpacingStyles from '../../styles/helpers/spacing';

import { AppContext } from '../../AppProvider';

import AssignerStyles from './__styles__/Assigner.styles';
import AssignerSchema from './Assigner.schema';
import { getFileUrl } from '../../utils/PropelFileUploader';

const DOCUMENT_FILE_TYPE = 'PDF';

export class Assigner extends PureComponent {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired
  };

  state = {
    item: {
      shipmentId: ''
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      shipmentDocumentTypes: [],
      item: this.props.item,
      totalItems: this.props.totalItems,
      currentItemIndex: this.props.currentItemIndex,
      shipmentIdTyped: ''
    };
  }

  componentDidMount() {
    const {
      referenceData: { shipmentDocumentTypes }
    } = this.context.globalProps;

    this.setState({ shipmentDocumentTypes, shipmentIdTyped: '' });
  }

  componentDidUpdate(prevProps) {
    const { currentItemIndex, totalItems } = this.props;

    this.setState({ currentItemIndex, totalItems });

    if (prevProps.item.id !== this.props.item.id) {
      const { item } = this.props;
      this.setState({ item, shipmentIdTyped: '' });
    }
  }

  _handleOnClose = () => {
    this.props.onClose();
  };

  _handleOnSave = async values => {
    const { onSave } = this.props;
    try {
      await onSave(values);
      return true;
    } catch {
      return false;
    }
  };

  _handlePrevNavigation = () => {
    this.props.onNavigate(this.props.currentItemIndex - 1);
  };

  _handleNextNavigation = () => {
    this.props.onNavigate(this.props.currentItemIndex + 1);
  };

  _handleViewDocument = async () => {
    const {
      item: { originalFileDownloadUrlSas, fileTypeId }
    } = this.state;

    const fileURL = await getFileUrl(originalFileDownloadUrlSas, fileTypeId);
    window.open(fileURL);
  };

  _getSuggestions = async value => {
    const shipmentIds = this.props.shipmentIds.map(shipmentId => ({
      label: shipmentId.id,
      value: shipmentId.id
    }));

    const suggestions = shipmentIds.filter(shipmentId =>
      RegExp(value).test(shipmentId.label)
    );

    return Promise.resolve(suggestions);
  };

  render() {
    const { classes, t, isSubmitting, loadingMoreFiles } = this.props;

    return (
      <Formik
        validationSchema={AssignerSchema({ t })}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...this.state.item
        }}
        onSubmit={async values => {
          this._handleOnSave(values);
        }}
        onReset={(values, actions) => {
          actions.resetForm();
          this._handleOnClose();
        }}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          dirty
        }) => (
          <Modal
            open={true}
            onClose={handleReset}
            classes={{
              root: classes.modalRoot,
              body: classes.modalBody,
              footer: classes.modalFooter
            }}
            body={
              <Fragment>
                <div className={classes.titleContainer}>
                  <p className={classnames(classes.headline7, classes.title)}>
                    {this.state.item.uploadedFileName}
                  </p>
                  <p className={classes.body2Light}>
                    {this.state.currentItemIndex + 1} of {this.state.totalItems}{' '}
                    {t('Files')}
                  </p>
                  {loadingMoreFiles ? (
                    <div className={classes.loadingMoreFilesContainer}>
                      <LoadingIndicator
                        classes={{ icon: classes.loadingMoreFilesIcon }}
                      />
                    </div>
                  ) : (
                    <Fragment>
                      <IconButton
                        className={classes.prevFileButton}
                        onClick={this._handlePrevNavigation}
                        disabled={!this.state.currentItemIndex}
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                      <IconButton
                        className={classes.nextFileButton}
                        onClick={this._handleNextNavigation}
                        disabled={
                          this.state.currentItemIndex + 1 ===
                          this.state.totalItems
                        }
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </Fragment>
                  )}
                </div>
                <div className={classes.bodyContainer}>
                  <Grid container spacing={24}>
                    <Grid item xs={6}>
                      <div className={classes.emptyPreview}>
                        <div className={classes.previewContainer}>
                          {this.state.item.fileTypeId === DOCUMENT_FILE_TYPE ? (
                            <PictureAsPdfIcon
                              className={classes.fileTypeIcon}
                            />
                          ) : (
                            <PhotoLibraryIcon
                              className={classes.fileTypeIcon}
                            />
                          )}
                        </div>
                        {this.state.item.originalFileDownloadUrlSas && (
                          <IconButton
                            color="primary"
                            className={classes.openInNewTabButton}
                            onClick={this._handleViewDocument}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        )}
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      container
                      spacing={32}
                      className={classes.form}
                    >
                      <Grid item xs={12}>
                        <Field
                          name="shipmentDocumentTypeId"
                          onChange={handleChange}
                          render={({ field, form }) => {
                            return (
                              <OutlinedSelectInput
                                value={values.shipmentDocumentTypeId}
                                field={field}
                                form={form}
                                label={t('Tag')}
                                options={this.state.shipmentDocumentTypes}
                              />
                            );
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="shipmentId"
                          onChange={handleChange}
                          render={({ field, form }) => {
                            return (
                              <Autocomplete
                                getSuggestions={this._getSuggestions}
                                onSuggestionSelected={shipment =>
                                  handleChange({
                                    target: {
                                      name: 'shipmentId',
                                      value: shipment.value
                                    }
                                  })
                                }
                                label={t('Shipment Number')}
                                value={
                                  values.shipmentId
                                    ? values.shipmentId + ''
                                    : this.state.shipmentIdTyped + ''
                                }
                                onChange={change =>
                                  this.setState({ shipmentIdTyped: change })
                                }
                                field={field}
                                form={form}
                                classes={{
                                  root: classes.noMarginBottom
                                }}
                              />
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </div>
              </Fragment>
            }
            footer={
              <Fragment>
                <Button
                  className={classes.button}
                  onClick={handleReset}
                  color={'primary'}
                  classes={{ label: classes.buttonLabel }}
                >
                  {t('Cancel')}
                </Button>
                <ProgressButton
                  onClick={handleSubmit}
                  className={classes.button}
                  classes={{
                    disabled: classes.buttonDisabled,
                    label: classes.buttonLabel
                  }}
                  color={'primary'}
                  disabled={!dirty || isSubmitting}
                  isSubmitting={isSubmitting}
                >
                  {t('Assign')}
                </ProgressButton>
              </Fragment>
            }
          />
        )}
      />
    );
  }
}

Assigner.contextType = AppContext;

const translatedAssigner = withNamespaces()(withApollo(Assigner));
export default withStyles(
  combineStyles(
    AssignerStyles,
    LayoutStyles,
    TypographyStyles,
    ButtonStyles,
    SpacingStyles
  )
)(translatedAssigner);
